export type Project = {
  slug: string;
  name: string;
  url: string;
  year: number;
  role: { pt: string; en: string };
  /** Single-line pitch shown in the InfoPanel beside the kiosk. */
  pitch: { pt: string; en: string };
  /** One-line proof/result shown below the pitch. Omit when no concrete metric exists. */
  outcome?: { pt: string; en: string };
  color: string;
  colorEnd: string;
  logo?: string;
  screenshot: string;
  frameable: boolean;
  exhibitIndex: number;
};

// `as const satisfies` keeps each `slug` typed as its literal so
// `ProjectSlug` below is a union — features.ts then references slugs
// with autocomplete + typo protection. The downside is the array's
// element type becomes a union of literal shapes (entries without an
// `outcome` field, for instance, lose access to it on the union side).
// To keep consumers seeing the uniform `Project` shape, we hold the
// constant in `projectsConst` and re-export it cast to `Project[]`.
const projectsConst = [
  {
    slug: 'click-saude',
    name: 'Click Saúde',
    url: 'https://clickonline.pt',
    year: 2024,
    role: { pt: 'Marca e website', en: 'Brand and website' },
    pitch: {
      pt: 'Fisioterapia, pilates e saúde da mulher no Faial',
      en: "Physiotherapy, pilates and women's health on Faial",
    },
    outcome: {
      pt: 'Equipa de 9 fisioterapeutas · desde 2011',
      en: 'Team of 9 physiotherapists · since 2011',
    },
    color: '#3aa14f',
    colorEnd: '#1f6a30',
    logo: '/logos/click.png',
    screenshot: '/screenshots/click-saude.avif',
    frameable: true,
    exhibitIndex: 0,
  },
  {
    slug: 'clinihorta',
    name: 'CliniHorta',
    url: 'https://clinihorta.pt',
    year: 2024,
    role: { pt: 'Website clínico', en: 'Clinical website' },
    pitch: {
      pt: 'Clínica médica na Horta, Faial',
      en: 'Medical clinic in Horta, Faial',
    },
    outcome: {
      pt: '19 especialidades · desde 1998',
      en: '19 specialities · since 1998',
    },
    color: '#e91e63',
    colorEnd: '#a8123f',
    logo: '/logos/clinihorta.jpg',
    screenshot: '/screenshots/clinihorta.avif',
    frameable: false,
    exhibitIndex: 1,
  },
  {
    slug: 'health2u',
    name: 'Health2U',
    url: 'https://health2u.wantek.pt',
    year: 2025,
    role: { pt: 'Plataforma de saúde', en: 'Health platform' },
    pitch: {
      pt: 'Teleconsultas em casa, Açores e Continente',
      en: 'Telehealth at home, Azores and mainland Portugal',
    },
    outcome: {
      pt: '23+ especialidades · videoconsulta',
      en: '23+ specialities · video consult',
    },
    color: '#d97334',
    colorEnd: '#a14e1e',
    logo: '/logos/health2u.png',
    screenshot: '/screenshots/health2u.avif',
    frameable: false,
    exhibitIndex: 2,
  },
  {
    slug: 'hu2i',
    name: 'HU2I',
    url: 'https://hu2i.pt',
    year: 2025,
    role: { pt: 'Marca e website', en: 'Brand and website' },
    pitch: {
      pt: 'Consultoria de financiamento europeu',
      en: 'European funding consultancy',
    },
    outcome: {
      pt: '99% de aprovação · 6M€+ garantidos',
      en: '99% approval rate · €6M+ secured',
    },
    color: '#4a6c8e',
    colorEnd: '#2a3f55',
    logo: '/logos/hu2i.png',
    screenshot: '/screenshots/hu2i.avif',
    frameable: false,
    exhibitIndex: 3,
  },
  {
    slug: 'pico-island-villa',
    name: 'Pico Island Villa',
    url: 'https://picoislandvilla.com',
    year: 2025,
    role: { pt: 'Website de alojamento', en: 'Hospitality website' },
    pitch: {
      pt: 'Villa de luxo na ilha do Pico',
      en: 'Luxury villa on Pico island',
    },
    outcome: {
      pt: '4 suítes · piscina infinita aquecida · 300 m²',
      en: '4 suites · heated infinity pool · 300 m²',
    },
    color: '#c4a979',
    colorEnd: '#8a7449',
    screenshot: '/screenshots/pico-island-villa.avif',
    frameable: false,
    exhibitIndex: 4,
  },
  {
    slug: 'connect-energy',
    name: 'Connect Energy',
    url: 'https://connectenergy.pt',
    year: 2026,
    role: { pt: 'Marca e website', en: 'Brand and website' },
    pitch: {
      pt: 'Energia, construção e engenharia em São Jorge',
      en: 'Energy, construction and engineering on São Jorge',
    },
    outcome: {
      pt: '20+ anos · 1 MWp instalado',
      en: '20+ years · 1 MWp installed',
    },
    color: '#c8a35c',
    colorEnd: '#8e6f35',
    screenshot: '/screenshots/connect-energy.avif',
    frameable: true,
    exhibitIndex: 5,
  },
  {
    slug: 'ficha',
    name: 'Ficha',
    url: 'https://ficha.pt',
    year: 2026,
    role: {
      pt: 'Plataforma de automação documental',
      en: 'Document automation platform',
    },
    pitch: {
      pt: 'Pré-preenchimento por IA da Ficha de Acompanhamento EEE para o PRR',
      en: 'AI-driven auto-fill of EEE forms for Portuguese PRR funding',
    },
    outcome: {
      pt: '51 campos automatizados · 83% menos tempo',
      en: '51 auto-filled fields · 83% time saved',
    },
    // Brand colour read from the live site's Tailwind `--primary` token —
    // a deep forest green that ties to the institutional/compliance tone
    // of PRR funding documentation. Dark variant approx 10pp lower L*.
    color: '#005c22',
    colorEnd: '#00420b',
    screenshot: '/screenshots/ficha.avif',
    // ficha.pt sends `Content-Security-Policy: frame-ancestors 'none'`
    // and `X-Frame-Options: DENY` — cannot be embedded in the journey
    // iframe exhibit, so falls back to the static screenshot.
    frameable: false,
    exhibitIndex: 6,
  },
  {
    slug: 'primis',
    name: 'Primis',
    url: 'https://primis.pt',
    year: 2026,
    role: { pt: 'Marca e website', en: 'Brand and website' },
    pitch: {
      pt: 'Implantologia, medicina dentária e especialidades médicas em Leiria',
      en: 'Implantology, dentistry and medical specialties in Leiria',
    },
    color: '#4e008e',
    colorEnd: '#460080',
    screenshot: '/screenshots/primis.avif',
    frameable: true,
    exhibitIndex: 7,
  },
] as const satisfies readonly Project[];

export type ProjectSlug = (typeof projectsConst)[number]['slug'];

export const projects: readonly Project[] = projectsConst;
